import classNames from 'classnames/bind';
import { getAreaCode } from 'common/utils';
import {
  TripType,
  getBizFlightUrl,
  getSearchLinkPair,
  tripTypes,
  useSearchParamsContext,
} from 'contexts/searchParams';
import { SubscriptionRoute } from 'hooks/usePriceChangesSubscriptions';
import { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import Tab from './Tab';
import Toast, { useToast } from './Toast';
import styles from './index.module.scss';
import PriceAlert from './price-alert';
import SelectSeat from './selectSeat';
import { Everywhere, MultiCity, OneWay, RoundTrip } from './tabContent';

const cx = classNames.bind(styles);
export const SeatOptionContext = createContext<
  Dispatch<SetStateAction<boolean>>
>(() => {});

export enum Position {
  MAIN,
  LIST,
  EVERYWHERE,
}

export interface ITabContent {
  clickCodePrefix: string;
  isMultiCityExpanded: boolean;
  handleMultiCityExpanded(): void;
}

const tabContentMap = new Map([
  [TripType.EVERYWHERE, Everywhere],
  [TripType.RT, RoundTrip],
  [TripType.OW, OneWay],
  [TripType.MD, MultiCity],
]);

const SEOUL_CITY_NAMES = ['서울', '인천', '김포'];

interface IProps {
  disablePriceAlert: boolean;
  expandMultiCity: boolean;
  isLarge?: boolean;
  isLoading?: boolean;
  position?: Position;
  searchParams?: SubscriptionRoute;
  showSeat?: boolean;
  initialize?: (isMo?: boolean) => void;
  onAfterSearch?: () => void;
}

const SearchBox = ({
  disablePriceAlert,
  expandMultiCity,
  isLarge = false,
  isLoading = false,
  position = Position.MAIN,
  searchParams,
  showSeat = false,
  initialize = () => {},
  onAfterSearch,
}: IProps) => {
  const [, as] = getSearchLinkPair();
  const router = useRouter();
  const {
    searchParams: {
      tripType: selectedTripType,
      itinerary,
      passengers,
      seatClass,
      stayLength,
      isDirect,
    },
    isDomestic,
    isToBizTrip,
    setIsToBizTrip,
    setTripType,
    setPassengers,
    setSeatClass,
  } = useSearchParamsContext();
  const clickCodePrefix = getAreaCode(position, selectedTripType);
  const [showSeatOption, toggleSeatOption] = useState(showSeat);
  const [isMultiCityExpanded, setIsMultiCityExpanded] = useState(
    expandMultiCity || !isLarge,
  );
  const [message, showToast] = useToast();

  const showBizgo =
    !isDomestic &&
    itinerary.length <= 3 &&
    selectedTripType !== TripType.EVERYWHERE;
  if (!showBizgo && isToBizTrip) setIsToBizTrip(false);

  const validate = () => {
    for (let itinIdx = 0; itinIdx < itinerary.length; itinIdx++) {
      const { departureCity, arrivalCity, departureDate } = itinerary[itinIdx];
      const prev = itinerary[itinIdx - 1];
      if (!(departureCity && arrivalCity)) {
        showToast('출발지와 도착지를 모두 입력해 주세요');
        return false;
      }
      if (
        !(
          selectedTripType === TripType.EVERYWHERE ||
          departureDate ||
          // 오는 날 미정
          (selectedTripType === TripType.RT &&
            itinIdx === 1 &&
            !isDomestic &&
            stayLength)
        )
      ) {
        showToast('여행 일정을 입력하세요');
        return false;
      }
      if (
        departureCity.code === arrivalCity.code &&
        !(selectedTripType === TripType.OW && departureCity.code === 'ICN') // 무착륙 관광 비행
      ) {
        showToast('출발지와 도착지는 다르게 입력해 주세요');
        return false;
      }
      if (
        departureCity?.cityCode === arrivalCity?.cityCode &&
        departureCity?.cityCode &&
        arrivalCity?.cityCode
      ) {
        showToast('출발지와 도착지의 도시는 서로 다르게 입력해 주세요');
        return false;
      }
      if (
        SEOUL_CITY_NAMES.includes(departureCity.name) &&
        SEOUL_CITY_NAMES.includes(arrivalCity.name)
      ) {
        showToast('출발지와 도착지의 도시는 서로 다르게 입력해 주세요');
        return false;
      }
      if (
        selectedTripType === TripType.MD &&
        departureCity.isDomestic &&
        arrivalCity.isDomestic
      ) {
        showToast('다구간 항공권 여정 조회는 국제선만 가능합니다');
        return false;
      }
      if (
        itinIdx > 0 &&
        prev.departureCity?.code === departureCity.code &&
        prev.arrivalCity?.code === arrivalCity.code
      ) {
        showToast(
          '출발지와 도착지가 동일한 여정이 있습니다. 다시 입력해 주세요.',
        );
        return false;
      }
    }
    return true;
  };

  const searchFlights = () => {
    if (!validate()) {
      return;
    }
    if (isToBizTrip) {
      const bizFlightUrl = getBizFlightUrl({
        itinerary,
        passengers,
        seatClass,
        stayLength,
        isDirect,
      });
      window.open(bizFlightUrl, '_blank');
    }
    const { asPath }: any = router;
    if (asPath === as) router.reload();
    else {
      // router.push(as); 작동 안되는 상황이 있음
      location.href = as;
    }
    if (onAfterSearch) onAfterSearch();
  };

  const TabContent = tabContentMap.get(selectedTripType);

  const handleMultiCityExpanded = () => {
    setIsMultiCityExpanded(!isMultiCityExpanded);
  };

  useEffect(() => {
    setIsMultiCityExpanded(expandMultiCity || !isLarge);
  }, [expandMultiCity, isLarge]);

  return (
    <div
      className={cx('searchBox')}
      data-triptype={selectedTripType}
      data-multicity-expanded={isMultiCityExpanded}
      data-position={
        position === Position.LIST
          ? 'list'
          : position === Position.EVERYWHERE
            ? 'everywhere'
            : 'main'
      }
    >
      <div className={cx('tablist')} role="tablist">
        {[TripType.RT, TripType.OW, TripType.MD].map(tripType => (
          <Tab
            key={`tab_${tripType}`}
            className={cx('tab')}
            tripType={tripType}
            active={
              selectedTripType === TripType.EVERYWHERE
                ? tripType === TripType.RT
                : tripType === selectedTripType
            }
            isDisabled={
              selectedTripType === TripType.EVERYWHERE &&
              tripType !== TripType.RT
            }
            clickCodePrefix={position === Position.LIST ? 'lst' : 'mai'}
            setTripType={
              selectedTripType === TripType.EVERYWHERE ? undefined : setTripType
            }
            title={tripTypes.get(tripType)}
            setIsMultiCityExpanded={setIsMultiCityExpanded}
          />
        ))}
      </div>

      <SeatOptionContext.Provider value={toggleSeatOption}>
        <div className={cx('tabpanel')} role="tabpanel">
          {TabContent ? (
            <TabContent
              clickCodePrefix={clickCodePrefix}
              isMultiCityExpanded={isMultiCityExpanded}
              handleMultiCityExpanded={handleMultiCityExpanded}
            />
          ) : null}
          {showSeatOption && (
            <div className={cx('select_seat')}>
              <SelectSeat
                isDomestic={isDomestic}
                passengers={passengers}
                seatClass={seatClass}
                tripType={selectedTripType}
                setPassengers={setPassengers}
                setSeatClass={setSeatClass}
                toggleSeatOption={toggleSeatOption}
              />
            </div>
          )}
          <div className={cx('sub_area')}>
            {showBizgo && (
              <div className={cx('biztrip')}>
                <label className={cx('toggle')}>
                  <input
                    type="checkbox"
                    checked={isToBizTrip}
                    data-event-nlog="click"
                    data-event-area-code={`${clickCodePrefix}.bizgo`}
                    onChange={() => {
                      setIsToBizTrip(!isToBizTrip);
                    }}
                  />
                  <span>해외 출장입니다</span>
                </label>
                <details className={cx('info')}>
                  <summary></summary>
                  <div role="tooltip">
                    출장 선호 항공편, 상용 우대 혜택이 포함된 해외항공권 검색
                    결과를 제공합니다. 검색 시 출장여행으로 이동합니다.
                  </div>
                </details>
              </div>
            )}
            {!disablePriceAlert && (
              <div className={cx('item')}>
                <PriceAlert
                  isLoading={isLoading}
                  searchParams={searchParams!}
                />
              </div>
            )}
          </div>
          {selectedTripType === TripType.MD && (
            <button
              className={cx('expand')}
              aria-expanded={isMultiCityExpanded}
              type="button"
              onClick={() => {
                handleMultiCityExpanded();
                initialize(!isLarge);
              }}
            >
              <span className={cx('text')}>
                {isMultiCityExpanded ? '접기' : '수정'}
              </span>
            </button>
          )}
          <button
            className={cx('search')}
            type="submit"
            data-event-nlog="click"
            data-event-area-code={`${clickCodePrefix}.search`}
            onClick={searchFlights}
          >
            <span className={cx('txt')}>항공권 검색</span>
          </button>
        </div>
      </SeatOptionContext.Provider>

      <Toast message={message} />
    </div>
  );
};

export default SearchBox;
